import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import logo from "@/assets/firstgiving.webp";
import { useToast } from "@/hooks/use-toast";
import { UserCircle } from "lucide-react";
import { MobileNav } from "./MobileNav";

export const Header = () => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    setProfile(data);
  };

  // Set up realtime listener for profile changes
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('profile-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          setProfile(payload.new as any);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
    navigate("/");
  };

  const handleDashboardClick = () => {
    navigate('/dashboard');
  };

  const handleStartFundraiser = () => {
    if (user) {
      navigate('/create-fundraiser');
    } else {
      navigate('/auth');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
      <div className="w-full px-3 sm:px-6 lg:px-8 xl:px-12">
        <div className="flex items-center justify-between h-16 sm:h-[72px] gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-4 lg:gap-8 xl:gap-12 flex-shrink-0">
            {/* Mobile Menu */}
            <MobileNav 
              user={user}
              onLogout={handleLogout}
              onStartFundraiser={handleStartFundraiser}
              onDashboardClick={handleDashboardClick}
            />
            
            <Link to="/" className="flex items-center">
              <img src={logo} alt="Firstgiving" width="200" height="48" className="h-7 sm:h-10 w-auto object-contain" />
            </Link>
            
            <nav className="hidden lg:flex items-center gap-6 xl:gap-10">
              <Link to="/explore" className="text-[15px] font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap">
                Explore Fundraisers
              </Link>
              <Link to="/organizations" className="text-[15px] font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap">
                Organizations
              </Link>
              <Link to="/stories" className="text-[15px] font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap">
                Stories
              </Link>
              <Link to="/about" className="text-[15px] font-medium text-foreground hover:text-primary transition-colors whitespace-nowrap">
                About
              </Link>
            </nav>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            {user ? (
              <>
                <Button variant="outline" onClick={handleDashboardClick} size="icon" className="rounded-full h-9 sm:h-11 w-9 sm:w-11 border-2">
                  <Avatar className="h-full w-full">
                    <AvatarImage src={profile?.avatar_url || ''} alt={profile?.full_name || 'User'} />
                    <AvatarFallback>
                      <UserCircle className="h-5 w-5 sm:h-6 sm:w-6" />
                    </AvatarFallback>
                  </Avatar>
                </Button>
                <Button onClick={handleLogout} variant="outline" size="default" className="hidden sm:inline-flex rounded-full px-3 sm:px-7 h-9 sm:h-11 font-medium border-2 text-[11px] sm:text-[14px] whitespace-nowrap">
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button asChild variant="outline" size="default" className="hidden sm:inline-flex rounded-full px-3 sm:px-7 h-9 sm:h-11 font-medium border-2 text-[11px] sm:text-[14px] whitespace-nowrap">
                  <Link to="/auth">Sign In</Link>
                </Button>
                <Button onClick={handleStartFundraiser} variant="default" size="default" className="rounded-full px-3 sm:px-7 h-9 sm:h-11 font-semibold text-[11px] sm:text-[14px] whitespace-nowrap">
                  Start Fundraiser
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
